import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Phone, CheckCircle, MapPin, Coffee } from 'lucide-react';
import { API } from '../App';
import { Button } from '../components/ui/button';
import FaqSection from '../components/FaqSection';
import useSeo from '../hooks/useSeo';
import seo from '../data/seo.json';

const HERO = 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1920&h=1080&fit=crop';
const EVENT_TYPES = ['Weddings', 'Corporate events', 'Graduations', 'Sports events', 'Community events', 'Family events'];

function CityPackages({ city }) {
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    axios.get(`${API}/event-settings`).then((res) => {
      setPackages(Object.entries(res.data.packages || {}).filter(([, p]) => p.enabled).map(([id, p]) => ({ id, ...p })));
    }).catch(() => {});
  }, []);
  if (!packages.length) return null;
  return (
    <section className="py-16 px-6 bg-[#F5F0E1]" data-testid="city-packages">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-serif text-3xl text-[#1A1A1A] mb-8">Coffee Catering Packages in {city.name}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {packages.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl p-6 border border-[#E8DFC9]" data-testid={`city-package-${p.id}`}>
              <p className="font-serif text-xl text-[#1A1A1A]">{p.name}</p>
              <p className="text-[#8B6914] font-bold text-3xl my-2">${p.price}<span className="text-sm text-[#5C5446] font-normal">{p.price_type === 'per_drink' ? ' /drink' : ' flat'}</span></p>
              <p className="text-sm text-[#5C5446]">{p.people_range} · {p.duration}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function CityPage() {
  const { slug } = useParams();
  const city = seo.cities.find((c) => c.slug === slug);
  const title = city ? `Coffee Catering & Mobile Espresso Bar in ${city.name}, ${city.state} | Espresso Beso` : '';
  const description = city ? `Mobile espresso bar and coffee catering in ${city.name}, ${city.state} for weddings, corporate events and family parties. Unlimited drinks, full barista service. Call (971) 227-0595.` : '';
  useSeo(`/coffee-catering/${slug}`, city && { title, description });
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!city) return <Navigate to="/" replace />;
  const nearby = seo.cities.filter((c) => c.slug !== slug);

  return (
    <div className="min-h-screen bg-white" data-testid="city-page">
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <img src={HERO} alt={`Mobile espresso bar in ${city.name}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A1A1A]/75" />
        <div className="relative max-w-5xl mx-auto px-6 py-24 text-white">
          <p className="flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4"><MapPin className="w-4 h-4" />{city.name}, {city.state}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6" data-testid="city-title">Coffee Catering in {city.name}, {city.state}</h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8" data-testid="city-intro">{city.intro}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/book-event" data-testid="city-book-event-link">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#1A1A1A] rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-auto"><Calendar className="w-5 h-5 mr-2" />Book Your Event</Button>
            </Link>
            <a href={`tel:${seo.phone}`} data-testid="city-call-link">
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-lg w-full sm:w-auto"><Phone className="w-5 h-5 mr-2" />Call {seo.phoneDisplay}</Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-serif text-3xl text-[#1A1A1A] mb-4">Events We Cater in {city.name}</h2>
            <ul className="space-y-3">
              {EVENT_TYPES.map((t) => <li key={t} className="flex items-center gap-3 text-[#5C5446]"><CheckCircle className="w-5 h-5 text-[#D4AF37]" />{t}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-3xl text-[#1A1A1A] mb-4">What's Included</h2>
            <ul className="space-y-3">
              {['Professional barista on site', 'Handcrafted espresso drinks & signature Beso mochas', 'Cold brews & seasonal specials', 'Flavored syrups, dairy & oat milk', 'Matcha upon request'].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#5C5446]"><Coffee className="w-5 h-5 text-[#8B1A1A]" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CityPackages city={city} />
      <FaqSection />

      <section className="py-16 px-6 bg-[#1A1A1A] text-white pb-32 md:pb-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-2xl mb-6">We Also Serve</h2>
          <div className="flex flex-wrap gap-2">
            {nearby.map((c) => (
              <Link key={c.slug} to={`/coffee-catering/${c.slug}`} className="text-sm rounded-full border border-white/20 px-4 py-2 text-white/80 hover:bg-white/10" data-testid={`nearby-city-${c.slug}`}>{c.name}, {c.state}</Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
